import {
  ArrowsRightLeftIcon,
  BuildingStorefrontIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import React from "react";
import {
  RelocationResult,
  RelocationSpaceDetail,
} from "../../types/aiChatClientV5";
import { isVacantTenant } from "../../utils/aiChatUtils";

interface RelocationCardProps {
  result: RelocationResult;
  /** Space number of the tenant holding the relocation clause (source). */
  targetSpace?: string;
  onHighlightSpace?: (spaceNumber: string, isVacant: boolean) => void;
  onHighlightAll?: (
    spaceNumbers: string[],
    detailsByNumber: Map<string, RelocationSpaceDetail>
  ) => void;
}

const formatCurrency = (
  value: number | null | undefined,
  currency?: string
): string => {
  if (value === null || value === undefined) return "—";
  const symbol =
    currency === "EUR" ? "€" : currency === "USD" ? "$" : currency || "";
  if (value >= 1_000_000) {
    return `${symbol}${(value / 1_000_000).toFixed(2)}M`;
  }
  if (value >= 1_000) {
    return `${symbol}${(value / 1_000).toFixed(1)}K`;
  }
  return `${symbol}${value.toLocaleString(undefined, {
    maximumFractionDigits: 0,
  })}`;
};

const formatArea = (
  value: number | null | undefined,
  unit?: string
): string => {
  if (value === null || value === undefined) return "—";
  const u = unit === "m2" ? "m²" : unit === "sqft" ? "SF" : unit || "";
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} ${u}`.trim();
};

const formatLeaseEnd = (date?: string | null): string => {
  if (!date) return "—";
  try {
    return new Date(date).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
    });
  } catch {
    return date;
  }
};

const RelocationCard: React.FC<RelocationCardProps> = ({
  result,
  targetSpace,
  onHighlightSpace,
  onHighlightAll,
}) => {
  const targetSpaceStr = targetSpace ? String(targetSpace) : "";
  const details = result.allowed_spaces_details || [];
  const vacantCount = details.filter((d) => isVacantTenant(d.tenant_name))
    .length;
  const occupiedCount = details.length - vacantCount;

  const handleHighlightAll = () => {
    if (!onHighlightAll) return;
    const numbers =
      result.allowed_space_numbers && result.allowed_space_numbers.length > 0
        ? result.allowed_space_numbers
        : details.map((d) => d.space_number);
    const map = new Map<string, RelocationSpaceDetail>();
    details.forEach((d) => map.set(String(d.space_number), d));
    onHighlightAll(numbers.map(String), map);
  };

  return (
    <div className="flex-none w-full max-w-[760px] bg-white border-l-4 border-violet-500 border-t border-r border-b border-gray-200 rounded-lg shadow-md p-4">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="bg-violet-600 p-1.5 rounded-full flex-shrink-0">
            <ArrowsRightLeftIcon className="h-4 w-4 text-white" />
          </div>
          <div className="min-w-0">
            <h5 className="text-sm font-semibold text-gray-800 truncate">
              {result.tenant_name}
              {result.store_mall_name && (
                <span className="text-gray-500 font-normal">
                  {" "}
                  · {result.store_mall_name}
                </span>
              )}
            </h5>
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              <span className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">
                Relocation Clause
              </span>
              <span className="text-[10px] text-gray-500">
                {details.length} allowed space{details.length > 1 ? "s" : ""} ·{" "}
                {occupiedCount} occupied · {vacantCount} vacant
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Section restriction */}
      {result.section_restriction && (
        <div className="mb-3 text-[11px] text-gray-700 bg-violet-50 border border-violet-100 rounded px-2 py-1.5">
          <span className="font-semibold text-violet-700">Restriction: </span>
          {result.section_restriction}
        </div>
      )}

      {/* Supporting quote */}
      {result.supporting_quote && (
        <blockquote className="mb-3 text-[11px] italic text-gray-600 border-l-2 border-violet-300 pl-2 py-1 bg-gray-50">
          “{result.supporting_quote}”
        </blockquote>
      )}

      {/* Spaces grid */}
      {details.length > 0 && (
        <div className="mb-3">
          <div className="text-[10px] text-gray-500 font-semibold mb-2 uppercase tracking-wide">
            Allowed spaces
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-[420px] overflow-y-auto pr-1">
            {details.map((space, idx) => {
              const vacant = isVacantTenant(space.tenant_name);
              const isTarget =
                !!targetSpaceStr &&
                String(space.space_number) === targetSpaceStr;
              return (
                <button
                  key={`${space.space_number}-${idx}`}
                  type="button"
                  onClick={() =>
                    onHighlightSpace?.(space.space_number, vacant)
                  }
                  className={`text-left rounded p-2 border transition-colors ${
                    isTarget
                      ? "bg-red-50 border-red-400 hover:bg-red-100"
                      : vacant
                        ? "bg-amber-50 border-amber-300 hover:bg-amber-100"
                        : "bg-gray-50 border-gray-200 hover:bg-violet-50 hover:border-violet-300"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-gray-800">
                      {space.space_number}
                    </span>
                    {isTarget ? (
                      <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-600 text-white">
                        SOURCE
                      </span>
                    ) : vacant ? (
                      <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-500 text-white">
                        <ExclamationTriangleIcon className="h-3 w-3" />
                        VACANT
                      </span>
                    ) : (
                      <BuildingStorefrontIcon className="h-3.5 w-3.5 text-gray-400" />
                    )}
                  </div>
                  <div
                    className={`text-[11px] mb-1 truncate ${
                      isTarget
                        ? "text-red-700 font-semibold"
                        : vacant
                          ? "text-amber-700 font-medium"
                          : "text-gray-700"
                    }`}
                  >
                    {space.tenant_name || "—"}
                  </div>
                  <div className="text-[10px] text-gray-600 space-y-0.5">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Area</span>
                      <span className="font-medium">
                        {formatArea(space.gla, space.area_unit)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Lease end</span>
                      <span className="font-medium">
                        {formatLeaseEnd(space.lease_end_date)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Rent</span>
                      <span className="font-medium">
                        {formatCurrency(space.total_rent, space.currency)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Sales R12</span>
                      <span className="font-medium">
                        {formatCurrency(space.sales_r12, space.currency)}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Footer actions */}
      {onHighlightAll && details.length > 0 && (
        <div className="pt-2 border-t border-gray-200">
          <button
            type="button"
            onClick={handleHighlightAll}
            className="w-full py-1.5 px-2 bg-violet-600 text-white text-[11px] font-semibold rounded hover:bg-violet-700 transition-colors"
          >
            Highlight all allowed spaces
          </button>
        </div>
      )}
    </div>
  );
};

export default RelocationCard;
